import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, take} from "rxjs";
import {OverlayService} from "../overlay/overlay.service";
import {
  TeacherGameSetupOverlayHeaderComponent
} from "../shared/teacher-game-setup-overlay-header/teacher-game-setup-overlay-header.component";
import {
  TeacherGameSetupOverlayFooterComponent
} from "../shared/teacher-game-setup-overlay-footer/teacher-game-setup-overlay-footer.component";
import {GameCategoriesSelectorComponent} from "../shared/game-categories-selector/game-categories-selector.component";
import {TeacherTimerSetupComponent} from "../shared/teacher-timer-setup/teacher-timer-setup.component";
import {GameCategoryService} from "./game-category.service";
import {OrganizationGameCategoryData} from "../interfaces/organization-game-category-data";
import {DataService} from "./data.service";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {GameSessionService} from "./game-session.service";
import {getActiveClassId} from "../store/selectors/teacher.selector";
import {untilDestroyed} from "@ngneat/until-destroy";

@Injectable({
  providedIn: 'root'
})
export class TeacherGameSetupService {

  private currentStep = 0;
  private readonly TOTAL_STEPS = 2;

  private currentOpenedOverlay?: number;

  maxTime = 0;

  activeCategories: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]); // store active selection here
  currentTime: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(
    private overlayService: OverlayService,
    private gameCategoryService: GameCategoryService,
    private data: DataService,
    private store: Store<AppState>,
    private gameSessionService: GameSessionService
  ) {

  }

  loadAllGameCategories(): Promise<OrganizationGameCategoryData> {
    return new Promise<OrganizationGameCategoryData>((resolve, reject) => {
      this.gameCategoryService.loadOrganizationGameCategoryData().then((data) => {
        resolve(data);
      });
    })

  }

  toggleActiveCategoryId(catId: number) {
    let currentState = this.activeCategories.value;

    if(currentState.indexOf(catId) === -1) {
      currentState.push(catId);
    } else {
      currentState = currentState.filter(el => el !== catId);
    }

    this.activeCategories.next(currentState);
  }

  getMaxTime() {
    this.store.select(getActiveClassId()).subscribe((classId) => {
      if(classId) {
        this.data.getGameTimeLeft(classId).subscribe((time) => {
          console.warn("this.maxTime", this.maxTime);
          this.maxTime = time;
        })
      }
    });
  }

  openWizard(): void {
    this.currentStep = 0;
    this.getMaxTime();

    this.openOverlayByStep(this.currentStep);
  }

  prevStep(): void {
    this.currentStep--;
    if(this.currentStep < 0) {
      this.cancel();
    } else {
      this.openOverlayByStep(this.currentStep);
    }
  }

  nextStep(): void {
    this.currentStep++;
    if(this.currentStep === this.TOTAL_STEPS) {
      this.submit();
    } else {
      this.openOverlayByStep(this.currentStep);
    }
  }

  submit(): void {
    this.gameCategoryService.updateRemoteGameCategories(this.activeCategories.value);
    this.gameSessionService.startGameSession(this.currentTime.value);
    this.closeAll();
  }

  cancel(): void {
    this.closeAll();
  }

  closeAll(): void {
    this.currentStep = 0;
    this.activeCategories.next([]);
    this.currentTime.next(0);
    if(this.currentOpenedOverlay !== undefined) {
      this.overlayService.removeComponent(this.currentOpenedOverlay);
    }
  }

  getActiveCategories(): Observable<number[]> {
    return this.activeCategories;
  }

  setCurrentTime(timeInSeconds: number): void {
    this.currentTime.next(timeInSeconds);
  }

  private resetSelection(): void {
    this.activeCategories.next([]);
  }

  private openOverlayByStep(stepIdx: number): void {
    if(this.currentOpenedOverlay !== undefined) {
      this.overlayService.removeComponent(this.currentOpenedOverlay);
    }

    switch (stepIdx) {
      case 0:
        this.loadAllGameCategories().then((data) => {
          this.currentOpenedOverlay = this.overlayService.openWindowOverlay({
            theme: "blue",
            componentHeader: {
              component: TeacherGameSetupOverlayHeaderComponent,
              data: {
                totalSteps: this.TOTAL_STEPS,
                step: stepIdx + 1
              }
            },
            componentFooter: {
              component: TeacherGameSetupOverlayFooterComponent,
              data: {
                prevLabel: "Abbrechen",
                nextLabel: "Weiter",
                step: stepIdx,
                resetCallback: () => {
                  this.resetSelection();
                },
                prevCallback: () => {
                  this.prevStep();
                },
                nextCallback: () => {
                  if(this.activeCategories.value.length > 0) {
                    this.nextStep();
                  }
                },
                selectUpdateListener: this.getActiveCategories()
              }
            },
            componentMain: {
              component: GameCategoriesSelectorComponent,
              data: {
                selectCallback: (catId: number) => {
                  this.toggleActiveCategoryId(catId);
                },
                currentStateListener: this.getActiveCategories(),
                data: data
              }
            }
          })
        })

        break;
      case 1:
        this.currentOpenedOverlay = this.overlayService.openWindowOverlay({
          theme: "blue",
          componentHeader: {
            component: TeacherGameSetupOverlayHeaderComponent,
            data: {
              totalSteps: this.TOTAL_STEPS,
              step: stepIdx + 1
            }
          },
          componentFooter: {
            component: TeacherGameSetupOverlayFooterComponent,
            data: {
              prevLabel: "Zurück",
              nextLabel: "Starten",
              step: stepIdx,
              resetCallback: () => {
                this.resetSelection();
              },
              prevCallback: () => {
                this.prevStep();
              },
              nextCallback: () => {
                if(this.activeCategories.value.length > 0) {
                  this.nextStep();
                }
              },
              selectUpdateListener: this.getActiveCategories()
            }
          },
          componentMain: {
            component: TeacherTimerSetupComponent,
            data: {
              valueChangedCallback: (timeInSeconds: number) => {
                console.log("Time: ", timeInSeconds);
                this.currentTime.next(timeInSeconds);
              },
              maxTime: this.maxTime
            }
          }
        })
        break;
      default:
        throw new Error("Invalid step index");
    }
  }

  startGameTimerNoCategories() {
    this.gameSessionService.startGameSession(15 * 60);
  }

  stopGameTimerNoCategories() {
    this.gameSessionService.endGameSession();
  }

}
